import { router } from "@inertiajs/react"
import {
  ArrowDown,
  ArrowUp,
  Coins,
  Minus,
  Receipt,
  Store,
  Users,
  type LucideIcon,
} from "lucide-react"

import { AdminLayout } from "@/components/layouts/admin-layout"
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { MainContainer } from "@/components/ui/container"
import { PageHeader } from "@/components/ui/headers"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { TableSection } from "@/components/ui/table-section"
import { formatMoney } from "@/lib/formatters"
import { t } from "@/lib/i18n"
import { cn } from "@/lib/utils"

type Comparison = {
  current: number
  previous: number
  deltaPercent: number | null
}

type TopLocation = {
  id: number
  name: string
  sales: number
  serviceFee: number
}

type Props = {
  period: "day" | "month" | "year"
  metrics: {
    totalSales: Comparison
    serviceFees: Comparison
    newLocations: Comparison
    newUsers: Comparison
  }
  topLocations: TopLocation[]
}

const PERIOD_OPTIONS = [
  { value: "day", labelKey: "ui.react.pages.admin.dashboards.show.period_day" },
  { value: "month", labelKey: "ui.react.pages.admin.dashboards.show.period_month" },
  { value: "year", labelKey: "ui.react.pages.admin.dashboards.show.period_year" },
] as const

function money(value: number) {
  return formatMoney(value, { currency: "MXN" })
}

function PeriodSelector({ period }: { period: Props["period"] }) {
  const change = (value: string) => {
    router.get("/", { period: value }, { preserveScroll: true, preserveState: true })
  }

  return (
    <div
      className="bg-muted inline-flex rounded-lg p-0.5"
      role="group"
      aria-label={t("ui.react.pages.admin.dashboards.show.subtitle")}
    >
      {PERIOD_OPTIONS.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => change(option.value)}
          aria-pressed={period === option.value}
          className={cn(
            "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
            period === option.value
              ? "bg-card text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          )}
        >
          {t(option.labelKey)}
        </button>
      ))}
    </div>
  )
}

function Delta({ metric }: { metric: Comparison }) {
  const { deltaPercent } = metric

  let indicator: React.ReactNode

  if (deltaPercent === null) {
    indicator = <span className="text-muted-foreground font-medium">—</span>
  } else {
    const Arrow = deltaPercent > 0 ? ArrowUp : deltaPercent < 0 ? ArrowDown : Minus
    const color =
      deltaPercent > 0
        ? "text-status-available"
        : deltaPercent < 0
          ? "text-destructive"
          : "text-muted-foreground"
    indicator = (
      <span className={cn("flex items-center gap-0.5 font-medium tabular-nums", color)}>
        <Arrow className="size-3" />
        {deltaPercent > 0 ? "+" : ""}
        {deltaPercent}%
      </span>
    )
  }

  return (
    <div className="flex items-center gap-1.5 text-xs">
      {indicator}
      <span className="text-muted-foreground">
        {t("ui.react.pages.admin.dashboards.show.vs_previous")}
      </span>
    </div>
  )
}

function MetricCard({
  label,
  icon: Icon,
  metric,
  format,
}: {
  label: string
  icon: LucideIcon
  metric: Comparison
  format: (value: number) => React.ReactNode
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <CardDescription>{label}</CardDescription>
          <Icon className="text-muted-foreground size-4 shrink-0" />
        </div>
        <CardTitle className="text-2xl tabular-nums">
          {format(metric.current)}
        </CardTitle>
        <Delta metric={metric} />
      </CardHeader>
    </Card>
  )
}

export default function AdminDashboardsShow({
  period,
  metrics,
  topLocations,
}: Props) {
  return (
    <AdminLayout>
      <MainContainer size="fullWidth">
        <PageHeader
          title={t("ui.navigation.home")}
          description={t("ui.react.pages.admin.dashboards.show.subtitle")}
          actions={<PeriodSelector period={period} />}
        />

        <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <MetricCard
            label={t("ui.react.pages.admin.dashboards.show.total_sales")}
            icon={Receipt}
            metric={metrics.totalSales}
            format={money}
          />
          <MetricCard
            label={t("ui.react.pages.admin.dashboards.show.service_fees")}
            icon={Coins}
            metric={metrics.serviceFees}
            format={money}
          />
          <MetricCard
            label={t("ui.react.pages.admin.dashboards.show.new_locations")}
            icon={Store}
            metric={metrics.newLocations}
            format={(value) => value}
          />
          <MetricCard
            label={t("ui.react.pages.admin.dashboards.show.new_users")}
            icon={Users}
            metric={metrics.newUsers}
            format={(value) => value}
          />
        </section>

        <TableSection
          title={t("ui.react.pages.admin.dashboards.show.top_locations")}
          description={t("ui.react.pages.admin.dashboards.show.top_locations_subtitle")}
        >
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">#</TableHead>
                <TableHead>
                  {t("ui.react.pages.admin.dashboards.show.location_col")}
                </TableHead>
                <TableHead className="text-right">
                  {t("ui.react.pages.admin.dashboards.show.sales_col")}
                </TableHead>
                <TableHead className="text-right">
                  {t("ui.react.pages.admin.dashboards.show.service_fee_col")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {topLocations.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    className="text-muted-foreground h-24 text-center"
                  >
                    {t("ui.react.pages.admin.dashboards.show.no_sales")}
                  </TableCell>
                </TableRow>
              ) : (
                topLocations.map((location, index) => (
                  <TableRow key={location.id}>
                    <TableCell className="text-muted-foreground tabular-nums">
                      {index + 1}
                    </TableCell>
                    <TableCell className="font-medium">{location.name}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {money(location.sales)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {money(location.serviceFee)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableSection>
      </MainContainer>
    </AdminLayout>
  )
}
